'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import {
  useCurrentUser,
  useMicroSource,
  type MicroContentItem,
} from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { cardReset } from '../_shared/styles';

/** Session-scoped dismissal — navigate-away-and-back doesn't re-prompt. */
const SESSION_DISMISS_KEY = 'akademi.micro.refresher.dismissed';
/** Cross-session time gate — a dismissal also mutes the prompt for 24h. */
const DISMISSED_AT_KEY = 'akademi.micro.refresher.dismissedAt';
const TIME_GATE_MS = 24 * 3600_000;

/**
 * "Resume where you left off" picker for micro content. Prefers the
 * learner's real in-progress rows ({@code progress.fetchMine}, newest
 * {@code lastSeenAt} first — one read, no per-item N+1); hosts without
 * {@code fetchMine} fall back to scanning the featured rail. Silently
 * hides when there's nothing in progress.
 *
 * <p>Gating (legacy resume-refresher pipeline): a dismiss button hides
 * it for the session (sessionStorage) AND for 24h across sessions
 * (localStorage timestamp). Legacy additionally gated on course
 * enrollment + {@code lastActiveAt}; {@code MicroSource} has no
 * enrollment surface, so that check is skipped here.
 */
export interface MicroResumeRefresherProps {
  title?: string;
  resumeLabel?: string;
  itemHrefTemplate?: string;
}

function isDismissed(): boolean {
  try {
    if (sessionStorage.getItem(SESSION_DISMISS_KEY)) return true;
    const at = Number(localStorage.getItem(DISMISSED_AT_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < TIME_GATE_MS;
  } catch {
    return false; // storage can throw in sandboxed iframes
  }
}

export function MicroResumeRefresher({
  title: authoredTitle,
  resumeLabel: authoredResumeLabel,
  itemHrefTemplate = '/lxp/micro-learning/{kindSlug}',
}: MicroResumeRefresherProps) {
  const { t } = useTranslation('site-builder-blocks');
  const title = authoredTitle ?? t('microResume.title');
  const resumeLabel = authoredResumeLabel ?? t('microResume.resume');
  const user = useCurrentUser<{ name?: string }>();
  const micro = useMicroSource();
  const [dismissed, setDismissed] = useState(false);

  const state = useAsyncResource<MicroContentItem>(
    async (signal) => {
      if (!user) return null;
      if (!micro?.progress) return null;
      // Effect-time (client-only), so storage reads can't split hydration.
      if (isDismissed()) return null;
      if (micro.progress.fetchMine) {
        const rows = await micro.progress.fetchMine(signal);
        const newest = rows
          .filter((r) => r.status === 'in-progress')
          .sort((a, b) => (b.lastSeenAt ?? '').localeCompare(a.lastSeenAt ?? ''))[0];
        if (!newest) return null;
        return micro.content.fetchById(newest.itemId, signal);
      }
      // Fallback for hosts without fetchMine: scan the featured rail for
      // the first item with an in-progress row.
      const featured = await micro.content.featured({ limit: 8, signal });
      for (const item of featured) {
        const progress = await micro.progress.fetchItem(item.id, signal);
        if (progress?.status === 'in-progress') return item;
      }
      return null;
    },
    [user, micro],
  );

  const dismiss = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
      localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    } catch {
      /* storage unavailable — the in-memory dismissal still applies */
    }
    setDismissed(true);
  };

  if (!user || dismissed) return null;

  if (!micro?.progress) {
    return (
      <SectionShell title={title}>
        <AdapterPlaceholder blockType="MicroResumeRefresher" adapterName="MicroSource.progress" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={title}>
        <SkeletonRow count={1} layout="list" />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') return null;

  const item = state.data;
  const href = itemHrefTemplate
    .replace('{kindSlug}', encodeURIComponent(item.kindSlug))
    .replace('{id}', encodeURIComponent(String(item.id)));
  return (
    <SectionShell title={title}>
      <div style={{ position: 'relative' }}>
        <Card asChild>
          <Link
            href={href}
            style={{
              ...cardReset,
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: 'clamp(16px, 2vw, 20px)',
              background: 'var(--color-brand-50, #eff6ff)',
              borderRadius: 'var(--radius-md, 12px)',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <div
              aria-hidden
              style={{
                width: 80,
                aspectRatio: '16 / 9',
                borderRadius: 'var(--radius-sm, 6px)',
                background: item.coverImageUrl
                  ? `url(${item.coverImageUrl}) center/cover no-repeat`
                  : 'var(--color-brand-100, #dbeafe)',
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: '11px',
                  letterSpacing: 'var(--font-tracking-widest, 0.08em)',
                  textTransform: 'uppercase',
                  color: 'var(--color-brand-700, #1d4ed8)',
                  fontFamily: 'var(--font-family-mono, monospace)',
                }}
              >
                {item.kind.replace(/_/g, ' ')}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-base, 15px)',
                  fontWeight: 600,
                  marginTop: 2,
                  paddingRight: 36,
                }}
              >
                {item.title}
              </div>
            </div>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-brand-700, #1d4ed8)',
                flexShrink: 0,
                marginRight: 28,
              }}
            >
              {resumeLabel}
            </span>
          </Link>
        </Card>
        {/* Sibling of the link (not nested) so dismissing never navigates. */}
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('microResume.dismiss')}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 28,
            height: 28,
            borderRadius: 999,
            border: 'none',
            background: 'transparent',
            color: 'var(--color-text-secondary, #64748b)',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    </SectionShell>
  );
}
